/**
 * RuleConditionsList — the conditions, on a rail, joined by visible connectors.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The old list left AND/OR implicit: rows simply stacked, and the only clue was
 * a toggle in the header far away. Printing the connector BETWEEN the rows is
 * the cheapest possible fix — the logic becomes something you read rather than
 * something you deduce.
 */

import { FC, Fragment, ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import type { FlowLayer, RuleNode } from '@trafficpulse/shared';

import RuleConditionRow, { type RuleConditionRowProps } from '../RuleConditionRow';
import type { GroupTag } from '../rule-condition/kind/condition-kind';
import type {
  ExcludeViewItem,
  LeafRuleNode,
} from '../../../utils/exclude-view-model';

/** RuleConditionRow only ever emits leaf nodes; narrow what it returns. */
const asLeaf = (node: RuleNode): LeafRuleNode | null =>
  node.kind === 'condition' || node.kind === 'ranking' || node.kind === 'tag'
    ? node
    : null;

const Connector: FC<{ op: 'AND' | 'OR' }> = ({ op }) => (
  <div className="flex items-center gap-2 py-0.5" aria-hidden>
    <span className="rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {op === 'AND' ? 'and' : 'or'}
    </span>
    <span className="h-px flex-1 bg-border/50" />
  </div>
);

const NotWrapper: FC<{ children: ReactNode; onRemove: () => void }> = ({
  children,
  onRemove,
}) => (
  <div className="rounded-md border border-warning/30 bg-warning/5 p-1.5">
    <div className="mb-1 flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-wide text-warning">
        Not — inverts what is inside
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove NOT"
        className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </div>
    {children}
  </div>
);

const AddPill: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-7 items-center gap-1 rounded-md border border-dashed border-border px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
  >
    <Plus className="h-3 w-3" aria-hidden />
    {label}
  </button>
);

export interface RuleConditionsListProps {
  /** The window line — belongs INSIDE this clause, it measures these rows. */
  footer?: ReactNode;
  items: ExcludeViewItem[];
  op: 'AND' | 'OR';
  layer: FlowLayer;
  /** Cube key the group's conditions read — for compare-mode real examples. */
  windowKey?: string | null;
  tags: GroupTag[];
  /** Row switches (the Automation: Raw score only, no ⇄ compare). */
  rowOptions?: Pick<RuleConditionRowProps, 'rankingMetrics' | 'allowCompare' | 'describe'>;
  onReplace: (next: ExcludeViewItem) => void;
  onRemove: (id: string) => void;
  onAddCondition: () => void;
  onAddNot: () => void;
}

export const RuleConditionsList: FC<RuleConditionsListProps> = ({
  footer,
  items,
  op,
  layer,
  windowKey = null,
  tags,
  rowOptions,
  onReplace,
  onRemove,
  onAddCondition,
  onAddNot,
}) => (
  <div className="ml-1 space-y-2 border-l-2 border-border pl-3.5">
    {items.length === 0 ? (
      <p className="text-[12.5px] text-muted-foreground">
        No conditions yet — this rule matches nothing until you add one.
      </p>
    ) : (
      items.map((item, i) => (
        <Fragment key={item.id}>
          {i > 0 ? <Connector op={op} /> : null}
          {item.kind === 'leaf' ? (
            <RuleConditionRow
              node={item.node}
              layer={layer}
              windowKey={windowKey}
              tags={tags}
              {...rowOptions}
              onChange={(next) => {
                const leaf = asLeaf(next);
                if (leaf) onReplace({ ...item, node: leaf });
              }}
              onRemove={() => onRemove(item.id)}
            />
          ) : (
            <NotWrapper onRemove={() => onRemove(item.id)}>
              <RuleConditionRow
                node={item.child}
                layer={layer}
                windowKey={windowKey}
                tags={tags}
                {...rowOptions}
                onChange={(next) => {
                  const leaf = asLeaf(next);
                  if (leaf) onReplace({ ...item, child: leaf });
                }}
                onRemove={() => onRemove(item.id)}
              />
            </NotWrapper>
          )}
        </Fragment>
      ))
    )}
    <div className="flex flex-wrap items-center gap-1.5">
      <AddPill label="Condition" onClick={onAddCondition} />
      <AddPill label="NOT" onClick={onAddNot} />
    </div>
    {footer}
  </div>
);

export default RuleConditionsList;
